import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard, EmptyState } from "./ChartCard";
import { chartTheme, heatmapColor } from "./chartTheme";
import { useMetricsAnalystLoad } from "./useMetricsQueries";
import { useMetricsFilter } from "./MetricsFilterContext";
import type {
  AnalystCaseBucket,
  AnalystDecisionRatio,
  AnalystLaneTimeBox,
  UtilizationCell,
} from "@/api/dealFlow/metrics";

// Analyst Load tab (DealFlow.ViewAllMetrics only, gated upstream).

export function AnalystLoadMetricsSection() {
  const { filter } = useMetricsFilter();
  const query = useMetricsAnalystLoad(filter, true);
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <Error />;
  const { casesPerAnalyst, timePerCasePerLane, decisionRatios, utilizationHeatmap } = query.data;

  return (
    <div
      className="grid grid-cols-1 gap-space-4 lg:grid-cols-2"
      data-testid="metrics-loaded"
    >
      <ChartCard
        title="Active cases per analyst"
        hint="Underwriting + Closing stages only."
        testId="chart-cases-per-analyst"
      >
        <CasesChart data={casesPerAnalyst} />
      </ChartCard>
      <ChartCard
        title="Time per case per lane"
        hint="Box plot (5-number summary) of days from stage entry to lane assessment."
        testId="chart-time-per-lane"
      >
        <TimePerLaneBoxPlot data={timePerCasePerLane} />
      </ChartCard>
      <ChartCard
        title="Decline vs approve"
        hint="Lane Pass / Fail ratio per analyst (last 90 days)."
        testId="chart-ratios"
      >
        <DecisionRatioChart data={decisionRatios} />
      </ChartCard>
      <ChartCard
        title="Analyst utilization (12 weeks)"
        hint="Active-cases × tier-weight heatmap."
        testId="chart-heatmap"
      >
        <UtilizationHeatmap data={utilizationHeatmap} />
      </ChartCard>
    </div>
  );
}

function CasesChart({ data }: { data: AnalystCaseBucket[] }) {
  if (data.length === 0) return <EmptyState label="No active analyst assignments." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} />
        <YAxis dataKey="displayName" type="category" width={140} stroke={chartTheme.axis} fontSize={11} />
        <Tooltip />
        <Bar dataKey="caseCount" fill={chartTheme.primary} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function TimePerLaneBoxPlot({ data }: { data: AnalystLaneTimeBox[] }) {
  // Recharts has no native box plot. We compose one from ComposedChart by
  // representing each (analyst, lane) cell as a Scatter point at the
  // median with an ErrorBar covering [min, max] and a thin Bar from Q1→Q3
  // for the IQR. Self-contained, ~30 lines.
  if (data.length === 0) return <EmptyState label="No lane assessments in the last 90 days." />;
  const rows = data.map((d) => ({
    name: `${d.displayName} · ${d.lane}`,
    median: d.medianDays,
    iqr: d.q3Days - d.q1Days,
    iqrStart: d.q1Days,
    whiskerLow: d.medianDays - d.minDays,
    whiskerHigh: d.maxDays - d.medianDays,
  }));
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={rows} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} label={{ value: "Days", position: "insideBottom" }} />
        <YAxis dataKey="name" type="category" width={180} stroke={chartTheme.axis} fontSize={11} />
        <Tooltip />
        <Bar dataKey="iqr" stackId="b" fill={chartTheme.primary} opacity={0.6} />
        <Scatter dataKey="median" fill={chartTheme.onyx}>
          <ErrorBar
            dataKey="whiskerLow"
            direction="x"
            width={6}
            stroke={chartTheme.axis}
          />
          <ErrorBar
            dataKey="whiskerHigh"
            direction="x"
            width={6}
            stroke={chartTheme.axis}
          />
        </Scatter>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function DecisionRatioChart({ data }: { data: AnalystDecisionRatio[] }) {
  if (data.length === 0) return <EmptyState label="No lane decisions to compare." />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ left: 16, right: 16 }}>
        <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" />
        <XAxis type="number" stroke={chartTheme.axis} />
        <YAxis dataKey="displayName" type="category" width={140} stroke={chartTheme.axis} fontSize={11} />
        <Tooltip />
        <Bar dataKey="passes" stackId="d" fill={chartTheme.success} />
        <Bar dataKey="fails" stackId="d" fill={chartTheme.error} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function UtilizationHeatmap({ data }: { data: UtilizationCell[] }) {
  // Native Recharts has no heatmap — built as a CSS grid colored from the
  // shared design-system palette. ~50 lines, lives next to its peers.
  if (data.length === 0) return <EmptyState label="No utilization data to display." />;
  const analysts = Array.from(new Set(data.map((c) => c.displayName))).sort();
  const weeks = Array.from(new Set(data.map((c) => c.weekStart))).sort();
  const max = data.reduce((m, c) => Math.max(m, c.weightedScore), 0);
  const lookup = new Map(data.map((c) => [`${c.displayName}|${c.weekStart}`, c] as const));

  return (
    <div className="overflow-x-auto">
      <table className="text-small">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white-pure px-space-2 py-space-1 text-left">Analyst</th>
            {weeks.map((w) => (
              <th key={w} className="px-space-1 py-space-1 text-center font-normal text-gray-mid">
                {w.slice(5)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {analysts.map((a) => (
            <tr key={a}>
              <td className="sticky left-0 bg-white-pure px-space-2 py-space-1 font-medium">
                {a}
              </td>
              {weeks.map((w) => {
                const cell = lookup.get(`${a}|${w}`);
                const score = cell?.weightedScore ?? 0;
                return (
                  <td
                    key={w}
                    className="h-8 w-8 text-center align-middle"
                    style={{ background: heatmapColor(score, max) }}
                    title={
                      cell
                        ? `${cell.displayName} · week of ${cell.weekStart}: ${cell.activeCases} cases, score ${cell.weightedScore.toFixed(1)}`
                        : "No activity"
                    }
                  >
                    <span className="text-[10px]">{score > 0 ? Math.round(score) : ""}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading analyst load…
    </div>
  );
}
function Error() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load analyst-load metrics.
    </div>
  );
}

